import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, timer, of } from 'rxjs';
import { map, catchError, retry, shareReplay, tap } from 'rxjs/operators';
import { 
  AccessPlan, 
  Payment, 
  AccessPlanControllerService, 
  PaymentControllerService,
  Installment
} from '../../../../core/generated';
import { DashboardSummary } from '../models/dashboard-summary.interface';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * PlanPaymentDataService
 * Centralized service for managing plans and payments data
 * Requirements: 15.5
 */
@Injectable({
  providedIn: 'root'
})
export class PlanPaymentDataService {
  private accessPlansService = inject(AccessPlanControllerService);
  private paymentsService = inject(PaymentControllerService);
  private snackBar = inject(MatSnackBar);

  // Cache subjects
  private plansSubject = new BehaviorSubject<AccessPlan[]>([]);
  private paymentsSubject = new BehaviorSubject<Payment[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public plans$ = this.plansSubject.asObservable();
  public payments$ = this.paymentsSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();

  // Cache timestamp
  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Load user plans with caching
   * Requirement: 15.5
   */
  loadUserPlans(cpfCnpj: string, forceRefresh: boolean = false): Observable<AccessPlan[]> {
    const now = Date.now();
    const isCacheValid = (now - this.lastFetchTime) < this.CACHE_DURATION;

    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && isCacheValid && this.plansSubject.value.length > 0) {
      return of(this.plansSubject.value);
    }

    this.loadingSubject.next(true);
    const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');

    return this.accessPlansService.findByCpfcnpj1(cleanCpfCnpj, 0, 100, 'id').pipe(
      // Retry logic with exponential backoff (Requirement 15.5)
      retry({
        count: 3,
        delay: (error, retryCount) => {
          console.warn(`Retry attempt ${retryCount} for loading plans`, error);
          return timer(Math.pow(2, retryCount) * 1000);
        }
      }),
      map(response => response.content || []),
      tap(plans => {
        this.plansSubject.next(plans);
        this.lastFetchTime = now;
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.handleError('Erro ao carregar planos', error);
        this.loadingSubject.next(false);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  /**
   * Load user payments with caching
   * Requirement: 15.5
   */
  loadUserPayments(cpfCnpj: string, forceRefresh: boolean = false): Observable<Payment[]> {
    const now = Date.now();
    const isCacheValid = (now - this.lastFetchTime) < this.CACHE_DURATION;

    // Return cached data if valid and not forcing refresh
    if (!forceRefresh && isCacheValid && this.paymentsSubject.value.length > 0) {
      return of(this.paymentsSubject.value);
    }

    this.loadingSubject.next(true);
    const cleanCpfCnpj = cpfCnpj.replace(/\D/g, '');

    return this.paymentsService.findByCpfcnpj(cleanCpfCnpj, 0, 100, 'id').pipe(
      // Retry logic with exponential backoff (Requirement 15.5)
      retry({
        count: 3,
        delay: (error, retryCount) => {
          console.warn(`Retry attempt ${retryCount} for loading payments`, error);
          return timer(Math.pow(2, retryCount) * 1000);
        }
      }),
      map(response => response.content || []),
      tap(payments => {
        this.paymentsSubject.next(payments);
        this.lastFetchTime = now;
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.handleError('Erro ao carregar pagamentos', error);
        this.loadingSubject.next(false);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  /**
   * Load installments for a specific payment
   * Requirement: 15.5
   */
  loadInstallments(paymentId: number): Observable<Installment[]> {
    return this.paymentsService.findInstallmentsByPayment(paymentId).pipe(
      retry({
        count: 2,
        delay: (error, retryCount) => timer(Math.pow(2, retryCount) * 1000)
      }),
      catchError(error => {
        this.handleError('Erro ao carregar parcelas', error);
        return of([]);
      })
    );
  }

  /**
   * Calculate dashboard summary from plans and payments
   * Requirements: 1.1, 1.2
   */
  calculateDashboardSummary(): Observable<DashboardSummary> {
    return combineLatest([
      this.plans$,
      this.payments$
    ]).pipe(
      map(([plans, payments]) => {
        const summary: DashboardSummary = {
          totalPlans: plans.length,
          activePlans: plans.filter(p => p.active).length,
          monthlyTotal: this.calculateMonthlyTotal(plans),
          nextDueDate: this.findNextDueDate(payments),
          overdueCount: this.countOverdueInstallments(payments),
          totalPaid: this.calculateTotalPaid(payments),
          totalPending: this.calculateTotalPending(payments)
        };
        return summary;
      })
    );
  }

  /**
   * Calculate total monthly value from active plans
   * Requirement: 1.2
   */
  private calculateMonthlyTotal(plans: AccessPlan[]): number {
    return plans
      .filter(p => p.active)
      .reduce((sum, plan) => {
        const planTotal = plan.items?.reduce((itemSum, item) => itemSum + (item.finalPrice || 0), 0) || 0;
        // Assuming plans are annual, divide by 12 for monthly
        return sum + (planTotal / 12);
      }, 0);
  }

  /**
   * Find the next due date from all payments
   * Requirement: 1.2
   */
  private findNextDueDate(payments: Payment[]): Date | null {
    const today = new Date();
    let nextDate: Date | null = null;

    payments.forEach(payment => {
      payment.installments?.forEach(installment => {
        if (installment.status !== 'PAID' && installment.dueDate) {
          const dueDate = new Date(installment.dueDate);
          if (dueDate >= today) {
            if (!nextDate || dueDate < nextDate) {
              nextDate = dueDate;
            }
          }
        }
      });
    });

    return nextDate;
  }

  /**
   * Count overdue installments across all payments
   * Requirement: 1.2
   */
  private countOverdueInstallments(payments: Payment[]): number {
    const today = new Date();
    let count = 0;

    payments.forEach(payment => {
      payment.installments?.forEach(installment => {
        if (installment.status !== 'PAID' && installment.dueDate) {
          const dueDate = new Date(installment.dueDate);
          if (dueDate < today) {
            count++;
          }
        }
      });
    });

    return count;
  }

  /**
   * Calculate total paid amount
   * Requirement: 1.2
   */
  private calculateTotalPaid(payments: Payment[]): number {
    return payments.reduce((sum, payment) => {
      const paidAmount = payment.installments
        ?.filter(i => i.status === 'PAID')
        .reduce((installmentSum, i) => installmentSum + (i.amount || 0), 0) || 0;
      return sum + paidAmount;
    }, 0);
  }

  /**
   * Calculate total pending amount
   * Requirement: 1.2
   */
  private calculateTotalPending(payments: Payment[]): number {
    return payments.reduce((sum, payment) => {
      const pendingAmount = payment.installments
        ?.filter(i => i.status !== 'PAID' && i.status !== 'CANCELLED_PAYMENT')
        .reduce((installmentSum, i) => installmentSum + (i.amount || 0), 0) || 0;
      return sum + pendingAmount;
    }, 0);
  }

  /**
   * Clear cache and force refresh
   */
  clearCache(): void {
    this.lastFetchTime = 0;
    this.plansSubject.next([]);
    this.paymentsSubject.next([]);
  }

  /**
   * Centralized error handling
   * Requirement: 15.5
   */
  private handleError(message: string, error: any): void {
    console.error(message, error);
    
    let userMessage = message;
    
    // Customize message based on error type
    if (error?.status === 404) {
      userMessage = 'Dados não encontrados';
    } else if (error?.status === 403) {
      userMessage = 'Você não tem permissão para acessar estes dados';
    } else if (error?.status === 500) {
      userMessage = 'Erro no servidor. Tente novamente mais tarde';
    } else if (error?.status === 0) {
      userMessage = 'Erro de conexão. Verifique sua internet';
    }
    
    this.snackBar.open(userMessage, 'Fechar', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }
}
